import { createContext, FC, useCallback, useState, useEffect } from 'react'
import { decodeToken } from 'react-jwt'
import { useImmer } from 'use-immer'

import useAuth from '@hook/shared/use-auth'

import dashboard from '@service/dashboard'
import filters from '@service/filters'
import useService from '@service/user'

import type {
  IFilter,
  Ifilters,
  IFilters,
  IFiltersProps,
  IDailyProcess,
  IPeriodsFilter,
  IProfileFilter
} from '@base/@types/providers/Shared/FiltersProvider'

import { TokenProps } from './UserAuthProvider'

const FiltersProvider: FC = ({ children }) => {
  const { token } = useAuth()
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE
  const [loading, setLoading] = useState(true)

  const [usersQuery, setUsersQuery] = useState('')

  // Lista crua de usuários (tela de Usuários); `data.users` é a lista {label, value}
  const [rawUsers, updateRawUsers] = useImmer<any[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [dailyProcess, updateDailyProcess] = useImmer<IDailyProcess>({
    idDailyProcessLog: 0,
    dataInicio: '',
    dataFim: '',
    status: ''
  })

  const [data, updateData] = useState<IFilters>({
    centers: [],
    distributionCenters: [],
    worlds: [],
    categories: [],
    compactCategories: [],
    compactCategoriesConfig: [],
    providersForDistribution: [],
    providersForPurchase: [],
    companies: [],
    productManagers: [],
    categoryManagers: [],
    profiles: [],
    inventoryStatus: [],
    yearBudget: [],
    monthBudget: [],
    historicBudget: [],
    operationBudget: [],
    deposits: [],
    periods: [],
    centerTypes: [],
    abcCurves: [],
    simulationTypes: [],
    visualization: [],
    dimension: [],
    managerOperations: [],
    followUpCards: 0,
    companiesConfig: [],
    productManagersConfig: [],
    categoryManagersConfig: [],
    worldsConfig: [],
    centersConfig: [],
    cluster: [],
    kpiStatus: [],
    gruposSeparacao: [],
    purchaseOrderType: [],
    purchaseOrderTypeReab: [],
    closingBase: [],
    typeOfAlert: [],
    purchases: [],
    utilizarCronograma: [],
    purchasesSelect: [],
    typeOfSupply: [],
    users: [],
    subCategories: [],
    compradores: [],
    solicitantes: [],
    productsForPurchase: []
  })

  const dimension = (): IFilter[] => {
    return [
      {
        label: 'Total',
        value: 'total'
      },
      {
        label: 'DU',
        value: 'du'
      }
    ]
  }

  const simNao = (): IFilter[] => {
    return [
      {
        label: 'Sim',
        value: '1'
      },
      {
        label: 'Não',
        value: '0'
      }
    ]
  }

  const typeOfSupplyOptions = (): IFilter[] => {
    return [
      { label: 'Distribuição', value: '105' },
      { label: 'Compra', value: '103' },
      { label: 'Compra Programada', value: '108' },
      { label: 'Reabastecimento Emergencial', value: '102' }
    ]
  }

  const visualization = (): IFilter[] => {
    return [
      {
        label: 'BT Leo Liquido',
        value: 'btLeoLiquido'
      },
      {
        label: 'Venda Liquida',
        value: 'vendaLiquida'
      },
      {
        label: 'Venda Bruta',
        value: 'vendaBruta'
      },
      {
        label: 'Margem',
        value: 'margem'
      }
    ]
  }

  const simulationTypes = (): IFilter[] => {
    return [
      {
        label: 'Compra por reais',
        value: 1
      },
      {
        label: 'Compra por volume',
        value: 2
      },
      {
        label: 'Compra por quantidade',
        value: 3
      }
    ]
  }

  const abcCurves = (): IFilter[] => {
    return [
      {
        label: 'A',
        value: 'A'
      },
      {
        label: 'B',
        value: 'B'
      },
      {
        label: 'C',
        value: 'C'
      },
      {
        label: 'D',
        value: 'D'
      }
    ]
  }

  const clusterValues = (): IFilter[] => {
    return [
      {
        label: 'P',
        value: 'P'
      },
      {
        label: 'M',
        value: 'M'
      },
      {
        label: 'G',
        value: 'G'
      },
      {
        label: 'CD',
        value: 'CD'
      }
    ]
  }

  const centerTypes = (): IFilter[] => {
    return [
      { value: 0, label: 'Loja' },
      { value: 1, label: 'CD' }
    ]
  }
  const gruupSeparationType = (): any => {
    return [
      {
        value: ['ZCHP', 'ZFOR', 'ZTAB', 'ZMAQ', 'ZMAD', 'ZSEC'],
        label: 'Chaparia'
      },
      {
        value: ['ZFER', 'ZQUI', 'ZTRI', 'ZALU', 'ZFMT'],
        label: 'Ferragem'
      }
    ]
  }

  const typesOfAlerts = (): IFilter[] => {
    return [
      {
        label: 'Estoque Arbitrado',
        value: 10
      },
      {
        label: 'Compra Mínima',
        value: 11
      },
      {
        label: 'Produto Similar',
        value: 12
      },
      {
        label: 'Poucos dias de venda',
        value: 13
      },
      {
        label: 'Produto antigo e venda baixa',
        value: 14
      },
      {
        label: 'Ok para compra',
        value: 15
      },
      {
        label: 'Estoque Baixo CD',
        value: 16
      },
      {
        label: 'Estoque Mínimo',
        value: 17
      },
      {
        label: 'Incremento Estoque',
        value: 18
      }
    ]
  }

  const parseCommonFilters = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id || filter.codigo,
      label: `${filter.id || filter.codigo} - ${filter.descricao}`
    }))
  }

  const parseTypesFilters = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id || filter.codigo,
      label: ` ${filter.descricao}`
    }))
  }
  const parseValueFilters = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter,
      label: ` ${filter}`
    }))
  }

  const parseValueFiltersUser = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id,
      label: ` ${filter.email}`
    }))
  }

  const parseCommonStatus = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.iniciais,
      label: `${filter.iniciais || filter.id} - ${filter.descricao}`
    }))
  }

  const parseCommonKpiStatus = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id,
      label: `${filter.iniciais || filter.id} - ${filter.descricao}`
    }))
  }

  const parseProfileFilters = (values: any[]): IProfileFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id,
      label: filter.nome,
      description: filter.descricao,
      access: filter.acesso
    }))
  }

  const parsePeriodsFilters = (values: any[]): IPeriodsFilter[] => {
    return values.map((filter: any, index: number) => ({
      ano: filter.ano,
      descricao: filter.descricao,
      mes: filter.mes,
      value: index + 1,
      label: `${index + 1} - ${filter.descricao}`
    }))
  }

  const getUsers = useCallback(async () => {
    setUsersLoading(true)

    try {
      const { data } = await useService.getAll()

      updateRawUsers((draft: any) => {
        draft.splice(0, draft.length, ...(data?.data || []))
      })
    } catch {
    } finally {
      setUsersLoading(false)
    }
  }, [updateRawUsers])

  const getFilters = useCallback(
    async ({
      centers: isCenters = false,
      distributionCenters: isDistributionCenters = false,
      worlds: isWorlds = false,
      categories: isCategories = false,
      compactCategories: isCompactCategories = false,
      compactCategoriesConfig: isCompactCategoriesConfig = false,
      providersForDistribution: isProvidersForDistribution = false,
      providersForPurchase: isProvidersForPurchase = false,
      companies: isCompanies = false,
      productManagers: isProductManagers = false,
      categoryManagers: isCategoryManagers = false,
      profiles: isProfiles = false,
      inventoryStatus: isInventoryStatus = false,
      yearBudget: isYearBudget = false,
      monthBudget: isMonthBudget = false,
      historicBudget: isHistoricBudget = false,
      operationBudget: isOperationBudget = false,
      deposits: isDeposits = false,
      periods: isPeriods = false,
      centerTypes: isCenterType = false,
      abcCurves: isAbcCurves = false,
      simulationTypes: isSimulationTypes = false,
      visualization: isVisualization = false,
      dimension: isDimension = false,
      managerOperations: isManagerOperations = false,
      followUpCards: isFollowUpCards = false,
      companiesConfig: isCompaniesConfig = false,
      productManagersConfig: isProductManagersConfig = false,
      categoryManagersConfig: isCategoryManagersConfig = false,
      worldsConfig: isWorldsConfig = false,
      centersConfig: isCentersConfig = false,
      cluster: isCluster = false,
      kpiStatus: isKpiStatus = false,
      gruposSeparacao: isGruposSeparacao = false,
      purchaseOrderType: isPurchaseOrderType = false,
      purchaseOrderTypeReab: isPurchaseOrderTypeReab = false,
      closingBase: isClosingBase = false,
      typeOfAlert: isTypeOfAlert = false,
      purchases: isPurchases = false,
      typeOfSupply: isTypeOfSupply = false,
      utilizarCronograma: isUtilizarCronograma = false,
      users: isUsers = false,
      subCategories: isSubcategories = false,
      compradores: isCompradores = false,
      solicitantes: isSolicitantes = false,
      productsForPurchase: isProductsForPurchase = false,
      // eslint-disable-next-line unused-imports/no-unused-vars
      qtd = null
    }: Ifilters) => {
      try {
        setLoading(true) // Inicia o carregamento

        // Lógica principal aqui

        if (token) {
          const centers =
            isCenters &&
            data.centers?.length === 0 &&
            (await filters
              .getCenters()
              .then(dataCenters => dataCenters.data?.data)
              .catch(() => []))

          const companies =
            isCompanies &&
            data.companies?.length === 0 &&
            (await filters
              .getCompanies()
              .then(dataCompanies => dataCompanies.data?.data)
              .catch(() => []))

          const categories =
            isCategories &&
            data.categories?.length === 0 &&
            (await filters
              .getCategories()
              .then(dataCategories => dataCategories.data?.data)
              .catch(() => []))

          const compactCategories =
            isCompactCategories &&
            data.compactCategories?.length === 0 &&
            (await filters
              .getCompactCategories()
              .then(dataCompactCategories => dataCompactCategories.data?.data)
              .catch(() => []))

          const compactCategoriesConfig =
            isCompactCategoriesConfig &&
            data.compactCategoriesConfig?.length === 0 &&
            (await filters
              .getCompactCategoriesConfig()
              .then(
                dataCompactCategoriesConfig =>
                  dataCompactCategoriesConfig.data?.data
              )
              .catch(() => []))

          const worlds =
            isWorlds &&
            data.worlds?.length === 0 &&
            (await filters
              .getWorlds()
              .then(dataWorlds => dataWorlds.data?.data)
              .catch(() => []))

          const profiles =
            isProfiles &&
            data.profiles?.length === 0 &&
            (await filters
              .getProfiles()
              .then(dataProfiles => dataProfiles.data?.data)
              .catch(() => []))

          const productManagers =
            isProductManagers &&
            data.productManagers?.length === 0 &&
            (await filters
              .getProductManagers()
              .then(dataProductManagers => dataProductManagers.data?.data)
              .catch(() => []))

          const categoryManagers =
            isCategoryManagers &&
            data.categoryManagers?.length === 0 &&
            (await filters
              .getCategoryManagers()
              .then(dataCategoryManagers => dataCategoryManagers.data?.data)
              .catch(() => []))

          const purchases =
            isPurchases &&
            data.purchases?.length === 0 &&
            (await filters
              .getProductsForDistribution()
              .then(dataPurchase => dataPurchase.data?.data)
              .catch(() => []))

          const providersForDistribution =
            isProvidersForDistribution &&
            data.providersForDistribution?.length === 0 &&
            (await filters
              .getProvidersForDistribution()
              .then(
                dataProvidersForDistribution =>
                  dataProvidersForDistribution.data?.data
              )
              .catch(() => []))

          const providersForPurchase =
            isProvidersForPurchase &&
            data.providersForPurchase?.length === 0 &&
            (await filters
              .getProvidersForPurchase()
              .then(
                dataProvidersForPurchase => dataProvidersForPurchase.data?.data
              )
              .catch(() => []))

          const distributionCenters =
            isDistributionCenters &&
            data.distributionCenters?.length === 0 &&
            (await filters
              .getDistributionCenters()
              .then(
                dataDistributionCenters => dataDistributionCenters.data?.data
              )
              .catch(() => []))

          const inventoryStatus =
            isInventoryStatus &&
            data.inventoryStatus?.length === 0 &&
            (await filters
              .getStatusInventory()
              .then(dataInventoryStatus => dataInventoryStatus.data?.data)
              .catch(() => []))

          const kpiStatus =
            isKpiStatus &&
            data.kpiStatus?.length === 0 &&
            (await filters
              .getStatusInventory()
              .then(dataInventoryStatus => dataInventoryStatus.data?.data)
              .catch(() => []))

          const yearBudget =
            isYearBudget &&
            data.yearBudget?.length === 0 &&
            (await filters
              .getBudgetYears()
              .then(dataYearBudget => dataYearBudget.data?.data)
              .catch(() => []))

          const monthBudget =
            isMonthBudget &&
            data.monthBudget?.length === 0 &&
            (await filters
              .getBudgetMonths()
              .then(dataMonthBudget => dataMonthBudget.data?.data)
              .catch(() => []))

          const historicBudget =
            isHistoricBudget &&
            data.historicBudget?.length === 0 &&
            (await filters
              .getStatusInventory()
              .then(dataHistoricBudget => dataHistoricBudget.data?.data)
              .catch(() => []))

          const managerOperations =
            isManagerOperations &&
            data.managerOperations?.length === 0 &&
            (await filters
              .getBudgetOperations()
              .then(dataManagerOperations => dataManagerOperations.data?.data)
              .catch(() => []))

          const operationBudget =
            isOperationBudget &&
            data.operationBudget?.length === 0 &&
            (await filters
              .getStatusInventory()
              .then(dataOperationBudget => dataOperationBudget.data?.data)
              .catch(() => []))

          const deposits =
            isDeposits &&
            data.deposits?.length === 0 &&
            (await filters
              .getDeposits()
              .then(dataDeposits => dataDeposits.data?.data)
              .catch(() => []))

          const periods =
            isPeriods &&
            data.periods?.length === 0 &&
            (await filters
              .getPeriods()
              .then(dataPeriods => dataPeriods.data?.data)
              .catch(() => []))

          const _followUpCards =
            isFollowUpCards &&
            data.followUpCards === 0 &&
            (await filters
              .getPeriods()
              .then(dataFollowUpCards => dataFollowUpCards.data?.data)
              .catch(() => []))

          const companiesConfig =
            isCompaniesConfig &&
            data.companiesConfig?.length === 0 &&
            (await filters
              .getCompaniesConfig()
              .then(dataCompaniesConfig => dataCompaniesConfig.data?.data)
              .catch(() => []))

          const productManagersConfig =
            isProductManagersConfig &&
            data.productManagersConfig?.length === 0 &&
            (await filters
              .getProductManagersConfig()
              .then(
                dataProductManagersConfig =>
                  dataProductManagersConfig.data?.data
              )
              .catch(() => []))

          const categoryManagersConfig =
            isCategoryManagersConfig &&
            data.categoryManagersConfig?.length === 0 &&
            (await filters
              .getCategoryManagersConfig()
              .then(
                dataCategoryManagersConfig =>
                  dataCategoryManagersConfig.data?.data
              )
              .catch(() => []))

          const worldsConfig =
            isWorldsConfig &&
            data.worldsConfig?.length === 0 &&
            (await filters
              .getWorldsConfig()
              .then(dataWorldsConfig => dataWorldsConfig.data?.data)
              .catch(() => []))

          const centersConfig =
            isCentersConfig &&
            data.centersConfig?.length === 0 &&
            (await filters
              .getCentersConfig()
              .then(dataCentersConfig => dataCentersConfig.data?.data)
              .catch(() => []))

          const purchaseOrderTypes =
            isPurchaseOrderType &&
            data.purchaseOrderType?.length === 0 &&
            (await filters
              .getPurchaseOrderType()
              .then(dataOrderTypes => dataOrderTypes.data?.data)
              .catch(() => []))

          const purchaseOrderTypeReab =
            isPurchaseOrderTypeReab &&
            data.purchaseOrderTypeReab?.length === 0 &&
            (await filters
              .getPurchaseOrderTypeReab()
              .then(dataOrderTypesReab => dataOrderTypesReab.data?.data)
              .catch(() => []))

          const closingBase =
            isClosingBase &&
            data.closingBase?.length === 0 &&
            (await filters
              .getClosingBase()
              .then(dataClosingBase => dataClosingBase.data?.data)
              .catch(() => []))

          const usersData =
            isUsers &&
            data.users?.length === 0 &&
            (await useService.getAll().then(dataUser => dataUser.data?.data))

          const subCategories =
            isSubcategories &&
            data.subCategories?.length === 0 &&
            (await filters
              .getSubCategoria()
              .then(subCategories => subCategories.data?.data)
              .catch(() => []))

          // Compradores e solicitantes usam o mesmo endpoint: uma única chamada
          // (solicitantes usa o id como valor — filtro por usuário solicitante)
          const needCompradores =
            isCompradores && data.compradores?.length === 0
          const needSolicitantes =
            isSolicitantes && data.solicitantes?.length === 0
          const compradoresData: any[] =
            needCompradores || needSolicitantes
              ? await filters
                  .getCompradores()
                  .then(compradores => compradores.data?.data || [])
                  .catch(() => [])
              : []

          const productsForPurchase =
            isProductsForPurchase &&
            data.productsForPurchase?.length === 0 &&
            (await filters
              .getProductsForPurchase()
              .then(dataProducts => dataProducts.data?.data)
              .catch(() => []))

          updateData(prevData => ({
            ...prevData,
            centersConfig:
              isCentersConfig && data.centersConfig?.length === 0
                ? parseCommonFilters(centersConfig)
                : data.centersConfig,

            worldsConfig:
              isWorldsConfig && data.worldsConfig?.length === 0
                ? parseCommonFilters(worldsConfig)
                : data.worldsConfig,

            categoryManagersConfig:
              isCategoryManagersConfig &&
              data.categoryManagersConfig?.length === 0
                ? parseCommonFilters(categoryManagersConfig)
                : data.categoryManagersConfig,

            productManagersConfig:
              isProductManagersConfig &&
              data.productManagersConfig?.length === 0
                ? parseCommonFilters(productManagersConfig)
                : data.productManagersConfig,

            companiesConfig:
              isCompaniesConfig && data.companiesConfig?.length === 0
                ? parseCommonFilters(companiesConfig)
                : data.companiesConfig,

            centers:
              isCenters && data.centers?.length === 0
                ? parseCommonFilters(centers)
                : data.centers,

            distributionCenters:
              isDistributionCenters && data.distributionCenters?.length === 0
                ? parseCommonFilters(distributionCenters)
                : data.distributionCenters,

            worlds:
              isWorlds && data.worlds?.length === 0
                ? parseCommonFilters(worlds)
                : data.worlds,

            categories:
              isCategories && data.categories?.length === 0
                ? parseCommonFilters(categories)
                : data.categories,

            compactCategories:
              isCompactCategories && data.compactCategories?.length === 0
                ? parseCommonFilters(compactCategories)
                : data.compactCategories,

            compactCategoriesConfig:
              isCompactCategoriesConfig &&
              data.compactCategoriesConfig?.length === 0
                ? parseCommonFilters(compactCategoriesConfig)
                : data.compactCategoriesConfig,

            purchases:
              isPurchases && data.purchases?.length === 0
                ? parseCommonFilters(purchases)
                : data.purchases,

            providersForDistribution:
              isProvidersForDistribution &&
              data.providersForDistribution?.length === 0
                ? parseCommonFilters(providersForDistribution)
                : data.providersForDistribution,

            providersForPurchase:
              isProvidersForPurchase && data.providersForPurchase?.length === 0
                ? parseCommonFilters(providersForPurchase)
                : data.providersForPurchase,

            companies:
              isCompanies && data.companies?.length === 0
                ? parseCommonFilters(companies)
                : data.companies,

            productManagers:
              isProductManagers && data.productManagers?.length === 0
                ? parseCommonFilters(productManagers)
                : data.productManagers,

            categoryManagers:
              isCategoryManagers && data.categoryManagers?.length === 0
                ? parseCommonFilters(categoryManagers)
                : data.categoryManagers,

            profiles:
              isProfiles && data.profiles?.length === 0
                ? parseProfileFilters(profiles)
                : data.profiles,

            inventoryStatus:
              isInventoryStatus && data.inventoryStatus?.length === 0
                ? parseCommonStatus(inventoryStatus)
                : data.inventoryStatus,

            kpiStatus:
              isKpiStatus && data.kpiStatus?.length === 0
                ? parseCommonKpiStatus(kpiStatus)
                : data.inventoryStatus,

            yearBudget:
              isYearBudget && data.yearBudget?.length === 0
                ? parseCommonFilters(yearBudget)
                : data.yearBudget,

            monthBudget:
              isMonthBudget && data.monthBudget?.length === 0
                ? parseCommonFilters(monthBudget)
                : data.monthBudget,

            historicBudget:
              isHistoricBudget && data.historicBudget?.length === 0
                ? parseCommonStatus(historicBudget)
                : data.historicBudget,

            operationBudget:
              isOperationBudget && data.operationBudget?.length === 0
                ? parseCommonFilters(operationBudget)
                : data.operationBudget,

            managerOperations:
              isManagerOperations && data.managerOperations?.length === 0
                ? parseCommonFilters(managerOperations)
                : data.managerOperations,

            deposits:
              isDeposits && data.deposits?.length === 0
                ? parseCommonFilters(deposits)
                : data.deposits,

            periods:
              isPeriods && data.periods?.length === 0
                ? parsePeriodsFilters(periods)
                : data.periods,

            centerTypes:
              isCenterType && data.centerTypes?.length === 0
                ? centerTypes()
                : data.centerTypes,

            abcCurves:
              isAbcCurves && data.abcCurves?.length === 0
                ? abcCurves()
                : data.abcCurves,

            cluster:
              isCluster && data.cluster?.length === 0
                ? clusterValues()
                : data.cluster,

            simulationTypes:
              isSimulationTypes && data.simulationTypes?.length === 0
                ? simulationTypes()
                : data.simulationTypes,

            visualization:
              isVisualization && data.visualization?.length === 0
                ? visualization()
                : data.visualization,

            dimension:
              isDimension && data.dimension?.length === 0
                ? dimension()
                : data.dimension,

            utilizarCronograma:
              isUtilizarCronograma && data.utilizarCronograma?.length === 0
                ? simNao()
                : data.utilizarCronograma,

            gruposSeparacao:
              isGruposSeparacao && data.gruposSeparacao?.length === 0
                ? gruupSeparationType()
                : data.gruposSeparacao,

            purchaseOrderType:
              isPurchaseOrderType && data.purchaseOrderType?.length === 0
                ? parseTypesFilters(purchaseOrderTypes)
                : data.purchaseOrderType,

            purchaseOrderTypeReab:
              isPurchaseOrderTypeReab &&
              data.purchaseOrderTypeReab?.length === 0
                ? parseTypesFilters(purchaseOrderTypeReab)
                : data.purchaseOrderTypeReab,

            closingBase:
              isClosingBase && data.closingBase?.length === 0
                ? parseValueFilters(closingBase)
                : data.closingBase,

            subCategories:
              isSubcategories && data.subCategories?.length === 0
                ? parseCommonFilters(subCategories)
                : data.subCategories,

            compradores: needCompradores
              ? compradoresData.map((data: any) => ({
                  label: data.compradorEmail,
                  value: data.compradorEmail
                }))
              : data.compradores,

            solicitantes: needSolicitantes
              ? compradoresData.map((data: any) => ({
                  label: data.compradorEmail,
                  value: data.id ?? data.usuarioId ?? data.compradorId
                }))
              : data.solicitantes,

            productsForPurchase:
              isProductsForPurchase && data.productsForPurchase?.length === 0
                ? parseCommonFilters(productsForPurchase || [])
                : data.productsForPurchase,

            typeOfAlert:
              isTypeOfAlert && data.typeOfAlert?.length === 0
                ? typesOfAlerts()
                : data.typeOfAlert,

            typeOfSupply:
              isTypeOfSupply && data.typeOfSupply?.length === 0
                ? typeOfSupplyOptions()
                : data.typeOfSupply,

            users:
              isUsers && data.users?.length === 0
                ? parseValueFiltersUser(usersData)
                : data.users
          }))
        }

        // Mais lógica assíncrona, se necessário
      } catch (error) {
      } finally {
        setTimeout(() => {
          setLoading(false) // Atualiza o loading após a conclusão e o timeout
        }, 1000) // Ajuste o tempo conforme necessário
      }
    },
    [
      token,
      data.centers,
      data.companies,
      data.categories,
      data.compactCategories,
      data.compactCategoriesConfig,
      data.worlds,
      data.profiles,
      data.productManagers,
      data.categoryManagers,
      data.purchases,
      data.providersForDistribution,
      data.providersForPurchase,
      data.distributionCenters,
      data.inventoryStatus,
      data.kpiStatus?.length,
      data.yearBudget,
      data.monthBudget,
      data.historicBudget,
      data.managerOperations,
      data.operationBudget,
      data.deposits,
      data.periods,
      data.followUpCards,
      data.companiesConfig,
      data.productManagersConfig,
      data.categoryManagersConfig,
      data.worldsConfig,
      data.centersConfig,
      data.purchaseOrderType,
      data.purchaseOrderTypeReab,
      data.closingBase,
      data.users,
      data.subCategories,
      data.compradores,
      data.solicitantes,
      data.productsForPurchase,
      data.centerTypes,
      data.abcCurves,
      data.cluster,
      data.simulationTypes,
      data.visualization,
      data.dimension,
      data.utilizarCronograma,
      data.gruposSeparacao,
      data.typeOfAlert,
      data.typeOfSupply
    ]
  )

  const getDailyProcess = useCallback(async () => {
    try {
      const { data: res } = await dashboard.getProcessDaily()

      const data = res.data

      updateDailyProcess(data)
    } catch {}
  }, [updateDailyProcess])

  useEffect(() => {
    const userInfos = JSON.parse(
      localStorage.getItem(
        `@@auth0spajs@@::${clientId}::${audience}::openid profile email offline_access`
      )
    )
    const decodedToken = decodeToken(userInfos?.body.access_token) as TokenProps

    if (decodedToken?.role) {
      getDailyProcess()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, getDailyProcess])

  return (
    <FiltersContext.Provider
      value={{
        data,
        setLoading,

        ...data,

        rawUsers,
        usersLoading,
        getUsers,
        dailyProcess,

        getFilters,

        usersQuery,
        setUsersQuery,

        loading
      }}
    >
      {children}
    </FiltersContext.Provider>
  )
}

export default FiltersProvider
export const FiltersContext = createContext<IFiltersProps>({} as IFiltersProps)
